// VenueHeader — above-fold section of /venue/[slug].
// /bolder pass: full-bleed poster hero (international-orange, reversed-out cream,
// hard offset shadow) replaces the plain title row. Stat strap + Next Show card
// flow below in a constrained inner column.

// Venue entry rules — the poster hero carries the PAGE-level ENTRY chip
// (2026-08-20, Michael: rules on the venue pages as a pop-out like merch).
// Fourth client surface on the ONE gate; the per-ROW chips in
// VenueEventList are unchanged beneath it.
import {
  policyChipForVenue, useVenuePolicies,
  rulesChipAria, rulesChipTitle,
} from '../lib/venue-policies-gate.js';

// Warm brand neutrals — never pure black/white.
const WARM_DARK = '#1A1A18';
const WARM_CREAM = '#F8F6F2';
const ORANGE = '#FF4F00';
const AMBER = '#D57800';
const AMBER_LIGHT = '#FFB778';
const EUCALYPTUS_LIGHT = '#5A7A68';
// Deeper eucalyptus used for CTAs where cream-on-green must clear AA 4.5:1.
// EUCALYPTUS_LIGHT only hits 4.40:1 with cream; this deepened green clears
// 7.62:1 and keeps the brand family intact.
const EUCALYPTUS_GREEN = '#395542';

// Scheme guard — mirrors Map.jsx / Events.jsx safeHref. Returns the URL only
// when it is a string starting with http:// or https://. Venue.html doesn't
// load Events.jsx so we inline rather than rely on window.safeHref.
const _safeHref = (u) => (typeof u === 'string' && /^https?:\/\//i.test(u)) ? u : null;

// Track-y small-cap label style — reused for "OUR SPINE" + address strap +
// chips. Keeps the poster's micro-type consistent.
const TRACKY_LABEL = {
  fontFamily: "'Be Vietnam Pro', sans-serif",
  fontWeight: 700,
  textTransform: 'uppercase',
};

// Reformat a YYYY-MM-DD ISO date into the row-style "TUE MAY 26" label.
// Used by the stat-strap "next:" cell so the date matches the formatting of
// every other date on the page — no raw ISO bleeding through to users.
function formatStatStrapDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00Z');
  if (isNaN(d.getTime())) return iso;
  const weekday = d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' }).toUpperCase();
  const month = d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' }).toUpperCase();
  const day = d.toLocaleDateString('en-US', { day: 'numeric', timeZone: 'UTC' });
  return `${weekday} ${month} ${day}`;
}

// Derive an address strap — short form, uppercase, comma-stripped.
// "1805 Geary Blvd, San Francisco, CA 94115" → "1805 GEARY · SAN FRANCISCO"
function formatAddressStrap(address, fallbackCity) {
  if (!address) return (fallbackCity || '').toUpperCase();
  const parts = address.split(',').map(s => s.trim());
  const street = (parts[0] || '')
    .replace(/\b(Blvd|Street|St|Avenue|Ave|Road|Rd|Drive|Dr|Way|Ln|Lane)\b\.?/i, '')
    .trim();
  const city = parts[1] || fallbackCity || '';
  return [street, city].filter(Boolean).join(' · ').toUpperCase();
}

// Format a Date or ISO string into "TUE MAY 26 · 7PM DOORS" style label
// for the Next Show card. Falls back gracefully when only a date is present.
function formatNextShowDate(nextEvent) {
  if (!nextEvent) return '';
  const iso = nextEvent.startDate || nextEvent.iso_date;
  if (iso) {
    const d = new Date(iso);
    if (!isNaN(d.getTime())) {
      const weekday = d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' }).toUpperCase();
      const month = d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' }).toUpperCase();
      const day = d.toLocaleDateString('en-US', { day: 'numeric', timeZone: 'UTC' });
      // Only show time when an explicit time component is in the ISO string.
      // Pure date strings like "2026-05-26" should NOT inject a midnight time.
      const hasTimeComponent = typeof iso === 'string' && /T\d{2}:/.test(iso);
      if (hasTimeComponent) {
        const localDate = new Date(iso); // local TZ for the doors time
        const h = localDate.getHours();
        if (!isNaN(h)) {
          const hour12 = ((h + 11) % 12) + 1;
          const meridiem = h >= 12 ? 'PM' : 'AM';
          return `${weekday} ${month} ${day} · ${hour12}${meridiem} DOORS`;
        }
      }
      return `${weekday} ${month} ${day}`;
    }
  }
  // Fall back to existing string date; prefix weekday if iso_date is present.
  if (nextEvent.iso_date) {
    const d = new Date(nextEvent.iso_date + 'T00:00:00Z');
    if (!isNaN(d.getTime())) {
      const weekday = d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' }).toUpperCase();
      return `${weekday} · ${nextEvent.date || ''}`.trim();
    }
  }
  return nextEvent.date || '';
}

// Page-level ENTRY chip for the poster's identity row. FLIP-morphs the
// same RulesPopover the row chips use — "pop out window like merch."
//
// ANATOMY: the POSTER's chip grammar (1.5px outline on international orange,
// label and outline in ONE ink: --on-international-orange at 5.74:1, because
// cream there is 3.05:1 and fails AA for 11px text),
// not the fog ink — fog has no AA pairing on the orange field. The rules
// identity survives as the leading 5×5 square tell + NO trailing arrow
// (square-before vs arrow-after, the MERCH sibling distinction). The square
// follows the label ink in all states here: the amber drift caution has no
// contrast on orange, and drift is fully told inside the popover (numbers
// down, caution plate, source brick).
// Gate-null → chip ABSENT, the poster byte-identical (spec §1). Fallback
// when window.RulesPopover hasn't loaded: the venue's own policy page.
function VenuePosterEntryChip({ venue, darkMode }) {
  const [triggerRect, setTriggerRect] = React.useState(null);
  const lookup = useVenuePolicies();
  const rulesPolicy = policyChipForVenue(lookup, venue.canonical);
  if (!rulesPolicy) return null;

  const handleClick = (e) => {
    e.stopPropagation();
    if (typeof window.RulesPopover === 'function') {
      setTriggerRect(e.currentTarget.getBoundingClientRect());
    } else {
      window.open(rulesPolicy.entry.source_url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      <button
        type="button"
        className="focus-double-ring"
        onClick={handleClick}
        aria-label={rulesChipAria(venue.canonical)}
        aria-haspopup="dialog"
        title={rulesChipTitle(venue.canonical)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '6px 14px', minHeight: 24,
          background: 'transparent',
          border: '1.5px solid currentColor',
          color: 'var(--on-international-orange)',
          ...TRACKY_LABEL,
          fontSize: 11,
          letterSpacing: 2,
          lineHeight: 1,
          cursor: 'pointer',
          transition: 'background 160ms ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(248,246,242,0.14)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        <span aria-hidden="true" style={{
          width: 5, height: 5, flex: '0 0 auto', background: 'currentColor',
        }} />
        ENTRY
      </button>
      {triggerRect && typeof window.RulesPopover === 'function' && (
        <window.RulesPopover
          event={{ venue: venue.canonical, city: venue.city }}
          entry={rulesPolicy.entry}
          state={rulesPolicy.state}
          darkMode={darkMode}
          triggerRect={triggerRect}
          onClose={() => setTriggerRect(null)}
        />
      )}
    </>
  );
}

function VenueHeader({ venue, heat, nextEvent, darkMode = true }) {
  // Show "OUR SPINE / VENUE 0X OF 07" corner label only on viewports ≥ 768px.
  // Computed once on mount via matchMedia — no resize listener needed for a
  // decorative label.
  const [showSpineLabel, setShowSpineLabel] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    // md breakpoint — see lib/breakpoints.js.
    const mq = (window.bp && window.bp.minWidth)
      ? window.bp.minWidth('md')
      : window.matchMedia('(min-width: 768px)');
    setShowSpineLabel(mq.matches);
  }, []);

  // Mobile gate for poster + stat strap + NEXT SHOW card adaptations.
  // Same `xs2` (640px) breakpoint as VenueEventList — shared boundary
  // across the page via lib/breakpoints.js. Live listener (vs single-
  // fire) so rotating a device or resizing a responsive devtool re-
  // flows the layout without a refresh.
  // One resolver used by BOTH the state initializer and the effect, so the
  // boundary cannot drift between them.
  const mobileMq = () => (window.bp && window.bp.maxWidth)
    ? window.bp.maxWidth('xs2')
    : window.matchMedia('(max-width: 639.98px)');
  // Seed from matchMedia rather than `false` (D19 follow-up, 2026-07-29). A
  // `useState(false)` + passive-effect correction paints one frame of DESKTOP
  // layout on every mobile device before snapping — visible jank, and a layout
  // shift on any element whose size depends on it. Mirrors the darkMode
  // initializer at components/VenuePage.jsx:1147, which already does this right.
  const [isMobile, setIsMobile] = React.useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return mobileMq().matches;
  });
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = mobileMq();
    const update = () => setIsMobile(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener('change', update);
    else if (mq.addListener) mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update);
      else if (mq.removeListener) mq.removeListener(update);
    };
  }, []);

  if (!venue) return null;
  const scaleColor = venue.scale === 'arena' || venue.scale === 'mid-large' ? ORANGE
    : venue.scale === 'intimate' ? '#7A99AC'
    : AMBER;

  // Stable spine index for the corner label. Total derived from array length
  // so adding a venue updates "VENUE X OF YY" in one place.
  // marquee-7 (2026-07-20) appended after the original 9 — order mirrors
  // VENUE_RAIL in VenueRail.jsx.
  const SPINE_VENUES = ['the-fillmore', 'bill-graham-civic-auditorium', 'chase-center', 'fox-theater-oakland', 'oakland-arena', 'levis-stadium', 'frost-amphitheater', 'the-catalyst', 'sweetwater-music-hall', 'the-warfield', 'the-masonic', 'the-regency-ballroom', 'the-independent', 'greek-theatre-berkeley', 'sap-center', 'shoreline-amphitheatre', 'bottom-of-the-hill', 'the-chapel', 'great-american-music-hall', 'the-midway'];
  const spineIdx = SPINE_VENUES.indexOf(venue.slug);
  const spineTotal = String(SPINE_VENUES.length).padStart(2, '0');
  const spineLabel = spineIdx >= 0 ? `VENUE ${String(spineIdx + 1).padStart(2, '0')} OF ${spineTotal}` : null;

  return (
    <>
      {/* ───── POSTER HERO ─────
        * Full-bleed (no maxWidth on this wrapper). Padding handles the inner
        * gutter. Margin-bottom clears the 8px hard shadow + breathing room.
        * Mobile: smaller padding (24-32px), 4px hard shadow (down from 8px so
        * the offset reads at small scale without dominating the viewport).
        */}
      <div style={{
        position: 'relative',
        background: ORANGE,
        color: WARM_CREAM,
        padding: isMobile ? 'clamp(24px, 7vw, 32px)' : 'clamp(32px, 6vw, 72px)',
        marginBottom: isMobile ? 24 : 32,
        boxShadow: isMobile ? `4px 4px 0 ${WARM_DARK}` : `8px 8px 0 ${WARM_DARK}`,
      }}>
        {/* SearchBar trigger — top-right corner of the poster hero,
          * visible on all viewports. Rendered before the OUR SPINE label
          * so the label shifts down when both are present. Guard ensures
          * we only mount when the SearchBar bundle has loaded. */}
        {typeof window !== 'undefined' && window.SearchBar && (
          <div style={{
            position: 'absolute',
            top: 'clamp(16px, 2.5vw, 24px)',
            right: isMobile ? 'clamp(16px, 5vw, 24px)' : 'clamp(32px, 6vw, 72px)',
          }}>
            <window.SearchBar darkMode={darkMode} isMobile={isMobile} />
          </div>
        )}

        {/* OUR SPINE corner label — desktop only. Decorative graphic
          * (aria-hidden); venue name + capacity already convey the same
          * info to AT, but visible 12px text still needs 4.5:1 — cream on
          * orange is 3.05:1, so it reads --on-international-orange (5.74:1).
          * Offset below the SearchBar trigger when it is present (~52px =
          * trigger height + 8px gap). */}
        {showSpineLabel && spineLabel && (
          <div aria-hidden="true" style={{
            position: 'absolute',
            top: (typeof window !== 'undefined' && window.SearchBar)
              ? 'clamp(68px, 8vw, 84px)'
              : 'clamp(24px, 3vw, 36px)',
            right: 'clamp(32px, 6vw, 72px)',
            ...TRACKY_LABEL,
            fontWeight: 800,
            fontSize: 12,
            letterSpacing: 2,
            lineHeight: 1,
            color: 'var(--on-international-orange)',
            fontVariantNumeric: 'tabular-nums',
          }}>OUR SPINE · {spineLabel}</div>
        )}

        {/* Massive venue name. Long names are allowed to wrap naturally on
          * narrow viewports — uppercase + the orange field keeps it readable.
          * Min 40px (was 48px) keeps poster scale on tight mobile but lets
          * 3-word names like SWEETWATER MUSIC HALL break to 2 lines cleanly.
          * Lighter -1 tracking on mobile — aggressive tightening reads worse
          * at smaller scale. Desktop preserves -2 for poster impact. */}
        <h1 style={{
          fontFamily: "'Be Vietnam Pro', sans-serif",
          fontWeight: 800,
          fontSize: 'clamp(40px, 12vw, 144px)',
          // 0.95 reads a hair more breathable than 0.9 at desktop sizes while
          // staying poster-tight. Mobile still feels stacked.
          lineHeight: 0.95,
          letterSpacing: isMobile ? -1 : -2,
          textTransform: 'uppercase',
          margin: 0,
          color: WARM_CREAM,
          // Wrap balance helps long multi-word names break at a sensible point.
          textWrap: 'balance',
        }}>{venue.canonical}</h1>

        {/* Address strap — small tracky type. 14px bold is NOT large text
          * (that starts at 18.66px bold), so it needs 4.5:1: cream on orange
          * is 3.05:1; --on-international-orange is 5.74:1. */}
        <div style={{
          marginTop: 'clamp(16px, 2.5vw, 28px)',
          ...TRACKY_LABEL,
          fontWeight: 800,
          fontSize: 14,
          // Hero strap = 3px tracking (top of the scale).
          letterSpacing: 3,
          lineHeight: 1.2,
          color: 'var(--on-international-orange)',
        }}>{formatAddressStrap(venue.address, venue.city)}</div>

        {/* Identity row — scale chip + page-level ENTRY chip, same chip
            grammar. flexWrap so tight mobile widths stack cleanly. */}
        <div style={{
          marginTop: 'clamp(20px, 2.5vw, 28px)',
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        }}>
          {/* Scale chip — dark ink on orange, transparent fill, border in the same ink */}
          <div style={{
            display: 'inline-block',
            padding: '6px 14px',
            border: '1.5px solid currentColor',
            color: 'var(--on-international-orange)',
            ...TRACKY_LABEL,
            fontSize: 11,
            letterSpacing: 2,
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
            // JetBrains Mono only on the numeric portion would be cleaner, but
            // Be Vietnam tabular-nums is consistent and one less font swap.
          }}>{venue.scale}{venue.capacity ? ` · ${venue.capacity.toLocaleString()} CAP` : ''}</div>
          <VenuePosterEntryChip venue={venue} darkMode={darkMode} />
        </div>
      </div>

      {/* ───── BELOW-POSTER CONTENT ───── */}
      <header style={{
        // Match the event list's 16px gutter on mobile so the left edges of
        // the stat strap, NEXT SHOW card, and event rows all align.
        padding: isMobile ? '0 16px 24px' : '0 24px 32px',
        maxWidth: 1100,
        margin: '0 auto',
      }}>
        {heat && (
          // Stat strap. Desktop: horizontal row, flex-wrap as a safety net.
          // Mobile: counts share row 1 (compact, comma-feel via 14px gap),
          // "NEXT: TUE MAY 26" wraps to row 2 so the dotted-rhythm of date
          // info doesn't get squeezed against the bigger counts.
          <div style={{
            ...(typeof window !== 'undefined' && window.glassStyle
              ? window.glassStyle(darkMode, 'panel')
              : { background: 'var(--surface-container, #1E2020)' }),
            padding: isMobile ? '14px 16px' : '16px 20px',
            borderRadius: 2,
            margin: isMobile ? '0 0 16px' : '0 0 20px',
          }}>
          <div style={{
            display: 'flex',
            gap: isMobile ? 14 : 18,
            flexWrap: 'wrap',
            alignItems: 'baseline',
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 13,
            lineHeight: 1.4,
            fontVariantNumeric: 'tabular-nums',
          }}>
            {heat.this_week_count > 0 && (
              <div><strong style={{
                fontSize: 'clamp(16px, 5vw, 18px)',
                color: 'var(--amber-gold-ink)',
              }}>{heat.this_week_count}</strong> this week</div>
            )}
            <div><strong style={{
              fontSize: 'clamp(16px, 5vw, 18px)',
            }}>{heat.upcoming_count}</strong> upcoming</div>
            {heat.next_event_date && (
              // AA-compliant fallback (was failing #888). CSS var resolves
              // to theme-correct value per :root[data-theme].
              // Date reformatted from raw ISO to row-style "TUE MAY 26" so it
              // doesn't read as a developer artifact in the strap.
              // Mobile: force this onto its own line via flex-basis 100%, so
              // counts stay paired and the date label gets full width.
              <div style={{
                color: 'var(--fog-blue-ink, #A3BDCC)',
                textTransform: 'uppercase',
                letterSpacing: 1,
                flexBasis: isMobile ? '100%' : 'auto',
                marginTop: isMobile ? 2 : 0,
              }}>next: {formatStatStrapDate(heat.next_event_date)}</div>
            )}
          </div>
          </div>
        )}

        {nextEvent && (
          // Inverted editorial block — dark in both themes so it punctuates
          // the page like a poster pull-quote. /bolder upgrades: huge title,
          // weekday + doors-time date strap, eucalyptus TICKETS CTA pinned
          // bottom-right on desktop. On mobile we flow the CTA into the
          // content stack as a full-width primary action — feels more
          // tappable and avoids the absolute-positioned button overlapping
          // a wrapped title.
          <div style={{
            marginTop: 28,
            padding: isMobile ? 'clamp(20px, 5vw, 24px)' : 'clamp(24px, 3vw, 36px)',
            background: WARM_DARK,
            border: `1px solid #2A2A26`,
            borderRadius: 0,
            boxShadow: `4px 4px 0 ${ORANGE}`,
            color: WARM_CREAM,
            position: 'relative',
          }}>
            {/* /e/ preview pages are keyed by generateSlug(title). VENUE_EVENTS
                rows carry event_hash + a meaningless array-index id but NOT the
                title-derived slug. Compute it client-side via window.eventSlug
                (lib/event-slug.js, byte-identical to scrapers/normalize.js).
                Same fix as VenueEventList.jsx. */}
            <a href={`/e/${nextEvent.slug || (typeof window !== 'undefined' && window.eventSlug ? window.eventSlug(nextEvent.title) : '') || nextEvent.id || nextEvent.event_hash}`} style={{
              display: 'block',
              textDecoration: 'none',
              color: WARM_CREAM,
            }}>
              <div style={{
                ...TRACKY_LABEL,
                fontSize: 11,
                // 2px for the label scale (was 1.4 — joins the rationalized
                // tracking scale: 1 body, 2 labels, 3 hero straps).
                letterSpacing: 2,
                lineHeight: 1,
                color: AMBER_LIGHT,
              }}>Up next</div>
              <div style={{
                fontFamily: "'Be Vietnam Pro', sans-serif",
                fontWeight: 800,
                // Min 28px keeps poster scale on mobile.
                fontSize: 'clamp(28px, 4vw, 48px)',
                letterSpacing: -1,
                // Hair more breathing room than 1.0 — bumps from cramped to
                // tight-poster.
                lineHeight: 1.02,
                textTransform: 'uppercase',
                marginTop: 10,
                color: WARM_CREAM,
                // The title is preserved as the source case via uppercase
                // styling — artist names like "Mk.gee" still render visually
                // as MK.GEE but a screen reader gets the original.
                textWrap: 'balance',
              }}>{nextEvent.title}</div>
              <div style={{
                marginTop: 12,
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontSize: 13,
                lineHeight: 1.4,
                letterSpacing: 1,
                color: AMBER_LIGHT,
                textTransform: 'uppercase',
                fontVariantNumeric: 'tabular-nums',
              }}>{formatNextShowDate(nextEvent)}</div>
            </a>

            {_safeHref(nextEvent.offers?.url) && (
              <a
                href={_safeHref(nextEvent.offers.url)}
                target="_blank"
                rel="noopener noreferrer"
                style={isMobile ? {
                  // Mobile: full-width primary CTA below the date strap.
                  // minHeight 44 + inline-flex centering guarantees the WCAG AA
                  // 44px tap target regardless of line-height/font-size shifts
                  // (was 40px tall with padding alone — /polish carryover fix).
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: 20,
                  padding: '14px 20px',
                  minHeight: 44,
                  // Deeper green for AA contrast: cream on #395542 is 7.62:1
                  // (was EUCALYPTUS_LIGHT 4.40:1, failed AA).
                  background: EUCALYPTUS_GREEN,
                  color: WARM_CREAM,
                  ...TRACKY_LABEL,
                  fontSize: 12,
                  letterSpacing: 2,
                  lineHeight: 1,
                  textDecoration: 'none',
                  boxShadow: `3px 3px 0 ${ORANGE}`,
                  borderRadius: 0,
                  textAlign: 'center',
                } : {
                  position: 'absolute',
                  right: 'clamp(20px, 3vw, 32px)',
                  bottom: 'clamp(20px, 3vw, 32px)',
                  padding: '12px 20px',
                  // Deeper green for AA contrast: cream on #395542 is 7.62:1
                  // (was EUCALYPTUS_LIGHT 4.40:1, failed AA).
                  background: EUCALYPTUS_GREEN,
                  color: WARM_CREAM,
                  ...TRACKY_LABEL,
                  fontSize: 11,
                  letterSpacing: 2,
                  lineHeight: 1,
                  textDecoration: 'none',
                  boxShadow: `3px 3px 0 ${ORANGE}`,
                  borderRadius: 0,
                }}
              >TICKETS →</a>
            )}
          </div>
        )}
      </header>
    </>
  );
}
window.VenueHeader = VenueHeader;
